import React, { useState, useEffect, useRef } from "react";
import { Plus, X, ChevronDown, ChevronRight, Sparkles, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * VaccinesPlanner — self-contained row-editor for the "Vaccines to schedule"
 * block on the visit page. Rendered inside the Advice & Follow-up section.
 *
 * Props:
 *   value       — current array of { name, date } rows
 *   onChange    — (nextArray) => void
 *   catalog     — string[] of vaccine names for the datalist (age-aware, fetched by parent)
 *   patientId   — patient_id used by the "Suggest Vaccine" button to fetch
 *                 due/overdue vaccines from the backend schedule
 *   defaultOpen — whether the panel is expanded on mount (default: true when
 *                 the list is empty, else false — auto-collapsed once saved).
 *
 * A row's default appointment date is "today + 14 days" (a sensible clinic
 * follow-up window). Filtering out empty rows happens client-side before POST
 * in the parent — this component intentionally keeps the raw editable state.
 */
export default function VaccinesPlanner({ value = [], onChange, catalog = [], patientId, defaultOpen, autoSuggest = false, onAutoSuggested }) {
  const initialOpen = typeof defaultOpen === "boolean" ? defaultOpen : (value?.length ?? 0) === 0;
  const [open, setOpen] = useState(initialOpen);
  const [suggesting, setSuggesting] = useState(false);
  const autoSuggestedRef = useRef(false);

  const addRow = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    const iso = d.toISOString().slice(0, 10);
    onChange([...(value || []), { name: "", date: iso }]);
    if (!open) setOpen(true);
  };

  const updateRow = (i, patch) => {
    const next = [...value];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  const removeRow = (i) => onChange(value.filter((_, idx) => idx !== i));

  // "Suggest Vaccine" — asks the backend which vaccines are DUE / OVERDUE
  // for this patient and appends any that aren't already in the row list.
  const suggest = async () => {
    if (!patientId) { toast.error("Save the patient first before suggesting"); return; }
    setSuggesting(true);
    try {
      const r = await api.get(`/vaccines/suggest/${patientId}`);
      const items = r.data?.suggestions || [];
      if (!items.length) {
        toast.success("No due vaccines to suggest — the patient is up to date");
        return;
      }
      const existing = new Set((value || []).map((v) => (v.name || "").trim().toLowerCase()));
      const added = [];
      for (const s of items) {
        const nm = (s.name || "").trim();
        if (!nm) continue;
        if (existing.has(nm.toLowerCase())) continue;
        added.push({ name: nm, date: s.date, reason: s.reason });
        existing.add(nm.toLowerCase());
      }
      if (!added.length) {
        toast.info("All suggested vaccines are already on the list");
        return;
      }
      onChange([...(value || []), ...added]);
      setOpen(true);
      toast.success(`Added ${added.length} suggested vaccine${added.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch suggestions");
    } finally {
      setSuggesting(false);
    }
  };

  const summary = (value || []).filter((v) => v.name).map((v) => v.name).join(", ");

  // Auto-fire "Suggest" once when the parent URL carries ?suggestVaccines=1
  // (this is how the Dashboard's Suggest Vaccine dialog deep-links here).
  useEffect(() => {
    if (!autoSuggest || autoSuggestedRef.current || !patientId) return;
    autoSuggestedRef.current = true;
    suggest();
    if (onAutoSuggested) onAutoSuggested();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSuggest, patientId]);

  return (
    <div className="border-t border-slate-200 pt-3 mt-3" data-testid="vaccines-planner">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 font-heading font-semibold text-sm text-slate-800 hover:text-slate-900"
          data-testid="vaccines-planner-toggle"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Vaccines to schedule (prints on Rx)
          {!open && (value?.length ?? 0) > 0 && (
            <span className="ml-1 text-[10px] font-normal text-slate-500 truncate max-w-[280px]" data-testid="vaccines-planner-summary">
              — {value.length} · {summary}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={suggest}
            disabled={suggesting || !patientId}
            className="text-xs px-2.5 py-1 rounded-md border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="vaccine-suggest-btn"
            title="Auto-suggest vaccines due/overdue for this patient"
          >
            {suggesting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Suggest Vaccine
          </button>
          <button
            type="button"
            onClick={addRow}
            className="text-xs px-2.5 py-1 rounded-md border border-pink-300 bg-pink-50 text-pink-700 hover:bg-pink-100 inline-flex items-center gap-1"
            data-testid="vaccine-add-btn"
          >
            <Plus size={12} /> Add vaccine
          </button>
        </div>
      </div>
      {open && (
        value.length === 0 ? (
          <div className="text-[11px] text-slate-500">
            No vaccines scheduled for this visit. Tap <em>Suggest Vaccine</em> for age-appropriate due doses, or <em>Add vaccine</em> to enter one manually.
          </div>
        ) : (
          <div className="space-y-2">
            {value.map((v, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-end" data-testid={`vaccine-row-${i}`}>
                <div className="flex-1 min-w-[220px]">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vaccine</label>
                  <input
                    list={`vaccine-list-${i}`}
                    value={v.name}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    placeholder="Start typing… e.g. Tdap, Influenza, HPV"
                    className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm"
                    data-testid={`vaccine-name-${i}`}
                  />
                  <datalist id={`vaccine-list-${i}`}>
                    {catalog.map((name) => <option key={name} value={name} />)}
                  </datalist>
                  {v.reason && (
                    <div className="text-[10px] text-indigo-700 mt-0.5" data-testid={`vaccine-reason-${i}`}>
                      Suggested — {v.reason}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Appointment date</label>
                  <input
                    type="date"
                    value={v.date || ""}
                    onChange={(e) => updateRow(i, { date: e.target.value })}
                    className="h-9 px-2 border border-slate-300 rounded-md text-sm"
                    data-testid={`vaccine-date-${i}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="h-9 px-2 border border-slate-300 rounded-md text-xs text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                  data-testid={`vaccine-remove-${i}`}
                  title="Remove this row"
                >
                  <X size={12} /> Remove
                </button>
              </div>
            ))}
            <div className="text-[10px] text-slate-500">
              These will appear in a "Vaccines scheduled" block on the printed prescription. Doctor decides brand/dose during the appointment.
            </div>
          </div>
        )
      )}
    </div>
  );
}
